// 2026-09-27 US-010: Loyalty customer detail (Screen 2). Every qualifying transaction in
// Received-date order, so each dashboard number can be audited.
import { Button, Card, Col, Progress, Result, Row, Table, Tag, Tooltip } from 'antd';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { PATHS } from '@routing/routes';
import { Roles } from 'common/constants';
import { AccountSelector } from '@features/Account';
import { LoyaltyHighUnitCount, LoyaltyPromoSize, LoyaltyTransactionItem } from 'common/models/LoyaltyTypes';
import { OperationStatus } from 'store/rootTypes';
import { GetLoyaltyCustomerDetail } from '../business/LoyaltyBusiness';
import { LoyaltySelector } from '../store/LoyaltySelector';
import { LoyaltyTheme } from './LoyaltyTheme';
import '../loyalty.css';

const renderUnitCount = (_: unknown, record: LoyaltyTransactionItem) => (
  <>
    {record.IsInvalidUnitCount ? (
      // AC-8: raw value shown, counted as 0. For a blank invoice the raw value is the box-details
      // count, so say where it came from.
      <>
        {record.UnitCountRaw}
        {record.UnitCountSource === 'BoxDetails' && <span className="loyalty-muted">(from box details)</span>}
        <Tag className="loyalty-tag-invalid">Invalid unit count</Tag>
      </>
    ) : (
      <>
        {record.Units}
        {record.UnitCountSource === 'BoxDetails' && <span className="loyalty-muted">(from box details)</span>}
        {record.UnitCountSource === 'None' && <span className="loyalty-muted">(no box details)</span>}
      </>
    )}
    {record.IsHighUnitCount && (
      <Tooltip title={`More than ${LoyaltyHighUnitCount} units — counted, but check for a typo`}>
        <Tag className="loyalty-tag-invalid">Check units</Tag>
      </Tooltip>
    )}
    {record.HasMultipleInvoices && <Tag className="loyalty-tag-multi">Multiple invoices – first used</Tag>}
  </>
);

// 2026-09-28 US-010: Reference No replaces Invoice No, "Unit count" is labelled QTY to match
// the dashboard, and the Batch No column is removed (Phase 2 claims replace batches).
const columns = [
  { title: 'Reference No', dataIndex: 'RefNo', key: 'RefNo' },
  { title: 'Received date', dataIndex: 'ReceivedDate', key: 'ReceivedDate' },
  { title: 'QTY', key: 'Qty', render: renderUnitCount },
  { title: 'Status', dataIndex: 'Status', key: 'Status' },
];

// 2026-09-28 US-010: four summary fields share one row (To next free is shown by the progress bar)
const SummaryField = ({ label, value }: { label: string; value: string | number | undefined }) => (
  <Col xs={12} md={6} className="loyalty-summary">
    <div className="loyalty-summary-label">{label}</div>
    <div className="loyalty-summary-value">{value}</div>
  </Col>
);

export const LoyaltyCustomerDetail = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const accountState = useSelector(AccountSelector);
  const loyaltyState = useSelector(LoyaltySelector);

  const isAdmin = accountState.RoleName === Roles.Admin;
  const customerKey = loyaltyState.SelectedCustomerKey;

  // AC-25/26: UX only - LoyaltyService.ShouldBeAdminUser is the control.
  useEffect(() => {
    if (!isAdmin) {
      history.push(PATHS.Home.path);
    }
  }, [accountState.RoleName]);

  const loadDetail = () => {
    if (isAdmin && customerKey) {
      dispatch(GetLoyaltyCustomerDetail(customerKey));
    }
  };

  useEffect(loadDetail, [customerKey]);

  const goBack = () => history.push(PATHS.LoyaltyDashboard.path);

  const detail = loyaltyState.CustomerDetail;
  const isLoading = loyaltyState.submitStatus === OperationStatus.pending;

  // AC-24: no key (direct navigation / new tab) or Found = false.
  const isNotFound = !customerKey || (!isLoading && detail?.Found === false);
  // QA LOW: a failed call showed an empty card with only a Back button.
  const isLoadFailed = !isNotFound && !detail && loyaltyState.submitStatus === OperationStatus.failed;
  // QA LOW: until the response arrives, show a loading card instead of an empty one.
  const isWaiting = !detail?.Found && !isLoadFailed;

  if (isLoadFailed) {
    return (
      <LoyaltyTheme>
        <div className="App">
          <div className="loyalty-page">
            <Result
              status="warning"
              title="Could not load this customer"
              subTitle="Please try again."
              extra={[
                <Button key="retry" type="primary" size="large" onClick={loadDetail}>
                  Retry
                </Button>,
                <Button key="back" size="large" onClick={goBack}>
                  Back
                </Button>,
              ]}
            />
          </div>
        </div>
      </LoyaltyTheme>
    );
  }

  return (
    <LoyaltyTheme>
      <div className="App">
        <div className="loyalty-page">
          <Row justify="center" align="middle">
            <h1>Loyalty customer</h1>
          </Row>

          {isNotFound ? (
            <Result
              status="404"
              title="Customer not found"
              extra={
                <Button type="primary" size="large" onClick={goBack}>
                  Back
                </Button>
              }
            />
          ) : (
            <Card title="Received by KFS" className="card-custom loyalty-card" loading={isWaiting}>
              {detail?.Found && (
                <>
                  <Row gutter={16}>
                    <SummaryField label="Name" value={detail.Name} />
                    <SummaryField label="Email" value={detail.Email} />
                    <SummaryField label="QTY" value={detail.Qty} />
                    <SummaryField label="Free earned" value={detail.FreeEarned} />
                  </Row>

                  {/* AC-23: QTY 34 -> 4/10; QTY 10 -> 0/10 */}
                  <div className="loyalty-progress">
                    <div className="loyalty-summary-label">Progress to next free</div>
                    <Progress
                      percent={(detail.ProgressUnits * 100) / LoyaltyPromoSize}
                      format={() => `${detail.ProgressUnits}/${LoyaltyPromoSize}`}
                    />
                  </div>

                  {/* AC-20: oldest received first, from the server; not paged so every unit stays visible. */}
                  <Table
                    dataSource={detail.Transactions}
                    columns={columns}
                    rowKey="RowNo"
                    size="middle"
                    pagination={false}
                    loading={isLoading}
                  />
                </>
              )}

              <Row justify="end" className="loyalty-actions">
                <Button size="large" onClick={goBack}>
                  Back
                </Button>
              </Row>
            </Card>
          )}
        </div>
      </div>
    </LoyaltyTheme>
  );
};
